<script setup lang="ts">
import { useI18n } from '@/i18n';
import { inviteRoleLabelKey, isInviteRole } from '@/lib/roles';
import { Alert, Prose } from '@dabble/quillui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const { t } = useI18n();

/**
 * Read invite context from the live query first, then the sessionStorage copy
 * that `pullSessionFromUrl` stashes. The query is stripped during the
 * cross-origin login round trip, so the session value is the reliable source on
 * the auth screens — mirrors `SignupView.readQueryOrSession`.
 */
function readQueryOrSession(key: string): string {
  const q = route?.query?.[key];
  if (q !== undefined && q !== null && q !== '') {
    return String(Array.isArray(q) ? q[0] : q).trim();
  }
  const s = sessionStorage.getItem(key);
  return s ? String(s).trim() : '';
}

const role = computed(() => readQueryOrSession('role'));

/** Only collaborators joining someone else's workspace see the banner. */
const isInvite = computed(() => isInviteRole(role.value));

const roleLabelKey = computed(() => inviteRoleLabelKey(role.value));

/** Editors get a pencil (they write); readers/reviewers get glasses (they read). */
const icon = computed(() => (role.value.trim().toLowerCase() === 'editor' ? 'edit' : 'glasses'));

const heading = computed(() =>
  roleLabelKey.value ? t('invited_heading', { role: t(roleLabelKey.value) }) : t('invited_heading_generic'),
);
</script>

<template>
  <Alert v-if="isInvite" variant="info" :icon-left="icon" :title="heading" class="auth-invite-banner">
    <Prose variant="inherit" size="small">{{ t('invited_free_note') }}</Prose>
  </Alert>
</template>

<style lang="scss" scoped>
.auth-invite-banner {
  width: 100%;
  margin-bottom: space('medium');
  text-align: left;
}
</style>
